import React, { useEffect, useState } from 'react';
import { sp } from './sponsorApi';
import { DatePickerFr } from '../../components/ui/DatePickerFr';
import { RefreshCw, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';

const LEVEL = {
  OK: { c: 'bg-emerald-600/30 text-emerald-300', l: 'À jour' },
  SOON: { c: 'bg-blue-600/30 text-blue-300', l: 'À renouveler bientôt' },
  URGENT: { c: 'bg-amber-600/30 text-amber-300', l: 'Renouvellement urgent' },
  EXPIRED: { c: 'bg-red-600/30 text-red-300', l: 'Expirée' },
  NONE: { c: 'bg-slate-700 text-slate-300', l: 'Aucune convention' },
};

export default function RenouvellementTab({ role }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ message: '', proposed_start_date: '', proposed_end_date: '' });
  const [msg, setMsg] = useState('');

  const load = async () => {
    setLoading(true);
    try { const r = await sp.get('/renouvellement'); setData(r.data); }
    catch { /* noop */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const request = async () => {
    setMsg('');
    try { await sp.post('/renouvellement/demander', form); setMsg('Demande transmise à O\'SCOP.'); await load(); }
    catch (e) { setMsg(e?.response?.data?.detail || 'Échec.'); }
  };

  if (loading) return <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /> Chargement…</div>;
  const lvl = LEVEL[data?.alert_level || 'NONE'];

  return (
    <div className="space-y-6" data-testid="renouvellement-tab">
      {msg && <p className="text-sm text-emerald-400" data-testid="renewal-msg">{msg}</p>}
      <section className="bg-slate-900 border border-slate-800 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-emerald-400"><RefreshCw className="w-5 h-5" /><h2 className="text-white font-semibold">Renouvellement de convention</h2></div>
          <span className={`text-xs px-2 py-0.5 rounded ${lvl.c}`} data-testid="renewal-level">{lvl.l}</span>
        </div>
        {!data?.convention && <p className="text-slate-400 text-sm" data-testid="renewal-empty">Aucune convention active à renouveler.</p>}
        {data?.convention && (
          <div className="space-y-2 text-sm" data-testid="renewal-info">
            <p className="text-white font-medium">{data.convention.title} <span className="text-slate-500 text-xs">({data.convention.reference})</span></p>
            <p className="text-slate-400">Fin de convention : <b className="text-white">{data.convention.end_date || '—'}</b>
              {data.days_remaining !== null && <span className="ml-2 text-slate-500">({data.days_remaining} jours restants)</span>}
            </p>
            {data.alert_level === 'EXPIRED' && <div className="flex items-center gap-2 text-red-300 text-xs"><AlertTriangle className="w-4 h-4" />La convention est expirée — demandez un renouvellement.</div>}
          </div>
        )}
      </section>

      {data?.renewal && (
        <div className="flex items-center gap-2 text-emerald-300 bg-emerald-500/10 rounded-lg p-4 text-sm" data-testid="renewal-existing">
          <CheckCircle2 className="w-5 h-5" />Demande de renouvellement en statut : <b>{data.renewal.status}</b>
        </div>
      )}

      {data?.convention && role === 'sponsor_owner' && !(data.renewal && data.renewal.status === 'REQUESTED') && (
        <section className="bg-slate-900 border border-slate-800 rounded-xl p-6" data-testid="renewal-form">
          <h3 className="text-white font-semibold mb-3">Demander le renouvellement</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <DatePickerFr value={form.proposed_start_date} onChange={(v) => setForm({ ...form, proposed_start_date: v })} id="renewal-start-input" className="w-full" />
            <DatePickerFr value={form.proposed_end_date} onChange={(v) => setForm({ ...form, proposed_end_date: v })} id="renewal-end-input" className="w-full" />
            <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Message (motivations, évolutions souhaitées…)" rows={2} data-testid="renewal-message-input" className="md:col-span-2 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" />
          </div>
          <button onClick={request} data-testid="renewal-submit-btn" className="mt-3 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold px-4 py-2 rounded-lg flex items-center gap-1"><RefreshCw className="w-4 h-4" />Envoyer la demande</button>
        </section>
      )}
    </div>
  );
}
